<template>
  <draggable-panel
    v-if="visible"
    :visible="true"
    :title="title"
    icon="el-icon-video-camera"
    :width="panelWidth"
    :height="panelHeight"
    :enable-expand="false"
    :enable-maximize="true"
    :enable-minimize="false"
    :initial-position="{ top: 60 }"
    :z-index="3000"
    @close="close"
  >
    <div class="vd">
      <div class="vd__video">
        <bridge-video-panel
          ref="panel"
          :bridge-id="bridgeId"
          default-layout="1x1"
          :show-channel-list="false"
          :show-channel-select="false"
          :show-layout-switch="true"
          :show-refresh="true"
          empty-text="该桥梁未接入视频（NVR 未开放 RTSP）"
          @active-change="onActiveChange"
          @channels-change="onChannelsChange"
        ></bridge-video-panel>
      </div>

      <!-- 右侧单列：通道图标组 + 云台 -->
      <div class="vd__rail">
        <div class="vd__rail-title">
          <span><i class="el-icon-video-camera"></i> 通道</span>
          <span class="vd__rail-count">可拉流 {{ pullableCount }}/{{ channels.length }}</span>
        </div>

        <div class="vd__chgrid">
          <button
            v-for="ch in channels"
            :key="ch.streamId"
            type="button"
            class="vd__chbtn"
            :class="{ 'is-active': active && active.streamId === ch.streamId, 'is-off': !ch.pullable }"
            :title="'NVR#' + ch.nvrId + ' · CH' + ch.channel + (ch.codec ? ' · ' + ch.codec : '') + (ch.pullable ? '' : '（未开放）')"
            @click="pickChannel(ch)"
          >
            <i class="el-icon-video-camera"></i>
            <span>{{ ch.channel }}</span>
          </button>
          <div v-if="!channels.length" class="vd__rail-empty">无通道</div>
        </div>

        <div class="vd__ptz">
          <ptz-control-panel
            v-if="active"
            :key="active.nvrId + '-' + active.channel"
            :nvr-id="active.nvrId"
            :channel="active.channel"
            :nvr-label="'NVR#' + active.nvrId"
          ></ptz-control-panel>
          <div v-else class="vd__ptz-empty">请选择一路可播放的通道后使用云台</div>
        </div>
      </div>
    </div>
  </draggable-panel>
</template>

<script>
import DraggablePanel from '@/components/common/DraggablePanel.vue'
import BridgeVideoPanel from './BridgeVideoPanel.vue'
import PtzControlPanel from './PtzControlPanel.vue'

export default {
  name: 'VideoDialog',
  components: { DraggablePanel, BridgeVideoPanel, PtzControlPanel },
  props: {
    visible: { type: Boolean, default: false },
    bridgeId: { type: [String, Number], default: '' },
    bridgeName: { type: String, default: '' }
  },
  data() {
    return { active: null, channels: [] }
  },
  computed: {
    title() {
      return '实时视频' + (this.bridgeName ? ' · ' + this.bridgeName : '')
    },
    // 自适应：宽高随浏览器窗口收缩，避免超出屏幕导致右侧被裁切
    panelWidth() {
      return 'min(1180px, calc(100vw - 150px))'
    },
    panelHeight() {
      return 'min(640px, calc(100vh - 140px))'
    },
    pullableCount() {
      return this.channels.filter(c => c.pullable).length
    }
  },
  methods: {
    onActiveChange(ch) {
      this.active = ch
    },
    onChannelsChange(list) {
      this.channels = list || []
    },
    pickChannel(ch) {
      if (!ch || !ch.pullable) return
      if (this.$refs.panel && this.$refs.panel.selectStream) {
        this.$refs.panel.selectStream(ch.streamId)
      }
    },
    close() {
      this.active = null
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
.vd { height: 100%; display: flex; background: #0f172a; box-sizing: border-box; }
.vd__video { flex: 1; min-width: 0; }

/* 右侧单列：通道图标组在上、云台在下 */
.vd__rail {
  flex: 0 0 248px; width: 248px; box-sizing: border-box;
  display: flex; flex-direction: column; min-height: 0;
  background: #1e293b; border-left: 1px solid #334155;
}
.vd__rail-title {
  flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between;
  gap: 8px; padding: 8px 12px; font-size: 12px; color: #94a3b8;
  border-bottom: 1px solid #334155;
}
.vd__rail-title i { margin-right: 4px; }
.vd__rail-count { color: #64748b; white-space: nowrap; }

/* 通道图标组 */
.vd__chgrid {
  flex: 0 0 auto; display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px;
  padding: 8px; max-height: 186px; overflow-y: auto; overflow-x: hidden;
}
.vd__chbtn {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px;
  height: 44px; padding: 0; border-radius: 6px; cursor: pointer;
  border: 1px solid #334155; background: #263243; color: #cbd5e1; font-size: 12px;
  transition: background .15s, border-color .15s, color .15s;
}
.vd__chbtn i { font-size: 15px; opacity: .9; }
.vd__chbtn:hover { background: #334155; color: #fff; }
.vd__chbtn.is-active { background: #2563eb; border-color: #2563eb; color: #fff; }
.vd__chbtn.is-active i { opacity: 1; }
.vd__chbtn.is-off { opacity: .4; cursor: not-allowed; }
.vd__rail-empty { grid-column: 1 / -1; color: #64748b; font-size: 12px; text-align: center; padding: 12px 0; }

/* 云台：占满剩余高度，避免留白 */
.vd__ptz { flex: 1 1 auto; min-height: 0; overflow-y: auto; overflow-x: hidden; border-top: 1px solid #334155; padding: 12px; }
.vd__ptz-empty { color: #94a3b8; font-size: 13px; text-align: center; padding: 18px 0; }
</style>
